import Button from "components/common/Button";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { useNavigationHandler } from "hooks/useNavigationHandler";
import { integrationActions, orderActions, vendorActions } from "redux/Ldb/actions";
import { AccountPermissions } from "types/permissions";

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGetBankAccounts from "hooks/integrationHooks/useGetBankAccounts";
import { useGetSalesSettings, useUpdateSalesSettings } from "hooks/salesHooks";
import SelectInput from "components/common/InputField/SelectInput";
import Checkbox from "components/common/Checkbox";
import useGetApAccounts from "hooks/integrationHooks/useGetApAccounts";
import Paper from "components/common/Paper";
import SelectInputWithSearch from "components/common/InputField/SelectInputWithSearch";

const IntegrationDetails = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { goBack } = useNavigationHandler({ fallbackPath: "/dashboard/integrations" });
    const { integrationName, integrationId } = useParams() || {};
    const [socialAccount, setSocialAccount] = useState<any>([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [payableAccounts, setPayableAccounts] = useState([]);
    const [incomeAccounts, setIncomeAccounts] = useState([]);
    const [assetAccounts, setAssetAccounts] = useState([]);
    const [fixedAssetAccounts, setFixedAssetAccounts] = useState([]);
    const [formattedArray, setFormattedArray] = useState([]);
    const [syncedAccount, setSyncedAccount] = useState<boolean>(false);
    const [disconnected, setDisconnected] = useState<boolean>(false);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [showButton, setShowButton] = useState(false);
    const [quickBooksOnlineAPAccount, setQuickBooksOnlineAPAccount] = useState<any>(null);
    const [shouldAutoSyncPOs, setShouldAutoSyncPOs] = useState(false);

    const [syncSalesOrders, setSyncSalesOrders] = useState(false);
    const [formData, setFormData] = useState<{
        quickBooksOnline: {
            shouldAutoSyncSales: boolean;
            channelsToSync?: {
                channel: string;
                depositBankAccountRef: {
                    name: string;
                    id: string;
                };
            }[];
        };
    }>({
        quickBooksOnline: {
            shouldAutoSyncSales: false,
            channelsToSync: [],
        },
    });

    const {
        fetchedPOSettingsSuccess,
        fetchingPOSettings,
        updatedPOSettingsSuccess,
        updatingPOSettings,
    } = useAppSelector((state) => state.order);

    const { updatingSalesSettings, updateSalesSettingsSuccess, updateSettings, resetSuccess } =
        useUpdateSalesSettings();
    const { fetchingSalesSettings, fetchSalesSettingsSuccess, refetch } = useGetSalesSettings();
    const getBankAccountsQuery = useGetBankAccounts();
    const getChartOfAccountsQuery = useGetApAccounts();

    const handleUpdateSalesSettings = () => {
        updateSettings({
            ...formData,
            quickBooksOnline: {
                ...formData.quickBooksOnline,
                shouldAutoSyncSales: syncSalesOrders,
                ...(syncSalesOrders
                    ? { channelsToSync: formData.quickBooksOnline.channelsToSync }
                    : {}),
            },
        });
    };

    const handleUpdatePoSettings = () => {
        const body = {
            // approversId: data?.approvers?.map((item) => item._id?.trim())?.filter(Boolean),
            // shouldAutoSendPOsToSuppliers: notification,
            ...(shouldAutoSyncPOs
                ? {
                      quickBooksOnline: {
                          shouldAutoSyncPOs: shouldAutoSyncPOs,
                          ...(quickBooksOnlineAPAccount?.name && quickBooksOnlineAPAccount?.id
                              ? {
                                    apAccountRef: {
                                        name: quickBooksOnlineAPAccount?.name,
                                        id: quickBooksOnlineAPAccount?.id,
                                    },
                                }
                              : {}),
                      },
                  }
                : {
                      quickBooksOnline: {
                          shouldAutoSyncPOs: false,
                      },
                  }),
        };
        dispatch(orderActions.updatePOSettings(body));
    };

    const handleChannelSync = (channel: string, value: string) => {
        setFormData((prev) => {
            const prevFormData = { ...prev };
            const prevChannelsToSync = prevFormData.quickBooksOnline.channelsToSync;
            const existingQuickbooksIndex = prevChannelsToSync.findIndex(
                (item) => item.channel === channel
            );

            if (existingQuickbooksIndex !== -1) {
                if (value === "None") {
                    prevChannelsToSync.splice(existingQuickbooksIndex, 1);
                } else {
                    prevChannelsToSync[existingQuickbooksIndex] = {
                        channel,
                        depositBankAccountRef: {
                            name:
                                getBankAccountsQuery?.data?.find((item) => item?.Id === value)
                                    ?.Name || "",
                            id: value,
                        },
                    };
                }
            } else {
                if (value === "None") {
                    return prev;
                }
                prevChannelsToSync.push({
                    channel,
                    depositBankAccountRef: {
                        name:
                            getBankAccountsQuery?.data?.find((item) => item?.Id === value)?.Name ||
                            "",
                        id: value,
                    },
                });
            }

            return {
                ...prev,
                quickBooksOnline: {
                    ...prev.quickBooksOnline,
                    channelsToSync: prevChannelsToSync,
                },
            };
        });
    };

    const handleUpdateSalesAndPoSettings = () => {
        handleUpdateSalesSettings();
        handleUpdatePoSettings();
    };

    useEffect(() => {
        if (!!fetchSalesSettingsSuccess) {
            const salesSettings = fetchSalesSettingsSuccess?.settings;
            const shouldAutoSync = salesSettings?.quickBooksOnline?.shouldAutoSyncSales;

            setSyncSalesOrders(shouldAutoSync);

            setFormData({
                quickBooksOnline: {
                    shouldAutoSyncSales: shouldAutoSync,
                    channelsToSync:
                        salesSettings?.quickBooksOnline?.channelsToSync?.map((item) => ({
                            channel: item?.channel,
                            depositBankAccountRef: {
                                name: item?.depositBankAccountRef?.name,
                                id: item?.depositBankAccountRef?.id,
                            },
                        })) || [],
                },
            });
        }
    }, [fetchSalesSettingsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedPOSettingsSuccess)) {
            setShouldAutoSyncPOs(
                fetchedPOSettingsSuccess?.settings?.quickBooksOnline?.shouldAutoSyncPOs || false
            );
            setQuickBooksOnlineAPAccount(
                fetchedPOSettingsSuccess?.settings?.quickBooksOnline?.apAccountRef || null
            );
        }
    }, [fetchedPOSettingsSuccess]);

    useEffect(() => {
        if (Boolean(updatedPOSettingsSuccess)) {
            dispatch(orderActions.getPOSettings());
        }
    }, [updatedPOSettingsSuccess, dispatch]);

    useEffect(() => {
        dispatch(orderActions.getPOSettings());
    }, []);

    const {
        fetchSocialAccount,
        fetchSocialAccountSuccess,
        setQuickbooksAccountSuccess,
        fetchingQuickbooksExpenseAccounts,
        fetchingQuickbooksApAccounts,
        fetchingQuickbooksAssetAccounts,
        fetchingQuickbooksIncomeAccounts,
        fetchedQuickbooksApAccountsSuccess,
        fetchedQuickbooksExpenseAccountsSuccess,
        fetchedQuickbooksAssetAccountsSuccess,
        fetchedQuickbooksIncomeAccountsSuccess,
        updateSocialAccount,
        updateSocialAccountSuccess,
        fetchingQbDesktopExpenseAccounts,
        fetchingQbDesktopIncomeAccounts,
        fetchingQbDesktopApAccounts,
        fetchingQbDesktopCurrentAssetAccounts,
        fetchedQbDesktopExpenseAccountsSuccess,
        fetchedQbDesktopIncomeAccountsSuccess,
        fetchedQbDesktopCurrentAssetAccountsSuccess,
        fetchedQbDesktopFixedAssetAccountsSuccess,
        fetchedQbDesktopApAccountsSuccess,
    } = useAppSelector((state) => state.integration);
    const { fetchedAllChannelsSuccess, manageConnectionSuccess, manageConnection } = useAppSelector(
        (state) => state.vendor
    );

    useEffect(() => {
        dispatch(vendorActions.getAllChannels());
    }, [dispatch]);

    const googleConnected = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "gmail"
    );
    const microsoftConnected = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "microsoft"
    );
    const quickBooksConnected = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "quickbooks"
    );
    const shopifyConnected = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "shopify"
    );
    const amazonConnected = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "amazon"
    );
    const qbDesktop = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "quickbooks-desktop"
    );
    const spsCommerce = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "sps-commerce"
    );
    const klaviyo = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "klaviyo"
    );
    const googleAds = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "google_ads"
    );
    const tiktok = fetchedAllChannelsSuccess?.channel?.find(
        (item) => item?.channel?.toLowerCase() === "tiktok_ads"
    );

    useEffect(() => {
        if (integrationId) {
            dispatch(integrationActions.fetchSocialAccount(integrationId));
        }
    }, [dispatch, integrationId]);

    const handleGmailDeactivate = () => {
        if (googleConnected?.active) {
            const body = {
                activate: false,
                socialId: googleConnected?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleShopifyDeactivate = () => {
        if (shopifyConnected?.active) {
            const body = {
                activate: false,
                socialId: shopifyConnected?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };

    const handleMicrosoftDeactivate = () => {
        if (microsoftConnected?.active) {
            const body = {
                activate: false,
                socialId: microsoftConnected?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleQuickbooksDeactivate = () => {
        if (quickBooksConnected?.active) {
            const body = {
                activate: false,
                socialId: quickBooksConnected?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleAmazonDeactivate = () => {
        if (amazonConnected?.active) {
            const body = {
                activate: false,
                socialId: amazonConnected?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleSpsCommerceDeactivate = () => {
        if (spsCommerce?.active) {
            const body = {
                activate: false,
                socialId: spsCommerce?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleQbDesktopDeactivate = () => {
        if (qbDesktop?.active) {
            const body = {
                activate: false,
                socialId: qbDesktop?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleKlaviyoDeactivate = () => {
        if (klaviyo?.active) {
            const body = {
                activate: false,
                socialId: klaviyo?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleGoogleAdsDeactivate = () => {
        if (googleAds?.active) {
            const body = {
                activate: false,
                socialId: googleAds?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };
    const handleTiktokDeactivate = () => {
        if (tiktok?.active) {
            const body = {
                activate: false,
                socialId: tiktok?._id,
            };
            dispatch(vendorActions.manageConnection(body));
        }
    };

    useEffect(() => {
        if (disconnected && integrationName.toLowerCase() === "google") {
            handleGmailDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "amazon") {
            handleAmazonDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "microsoft") {
            handleMicrosoftDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "quickbooks") {
            handleQuickbooksDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "shopify") {
            handleShopifyDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "quickbooks-desktop") {
            handleQbDesktopDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "sps-commerce") {
            handleSpsCommerceDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "klaviyo") {
            handleKlaviyoDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "google-ads") {
            handleGoogleAdsDeactivate();
        }
        if (disconnected && integrationName.toLowerCase() === "tiktok-shop") {
            handleTiktokDeactivate();
        }
    }, [disconnected, integrationName]);

    useEffect(() => {
        if (Boolean(manageConnectionSuccess)) {
            setDisconnected(false);
            dispatch(vendorActions.resetManageConnectionSuccess());
            dispatch(vendorActions.getAllChannels());
            navigate("/dashboard/integrations", { state: { internalNavigation: true } });
        }
    }, [dispatch, manageConnectionSuccess]);

    useEffect(() => {
        if (Boolean(fetchSocialAccountSuccess)) {
            setSocialAccount(fetchSocialAccountSuccess?.account);
        }
    }, [dispatch, fetchSocialAccountSuccess]);

    const SocialData = [
        {
            name: "Quickbooks",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame_2611386_ievdjs.svg",
        },
        {
            name: "Google",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/gmail_1_zya4ai.svg",
        },
        {
            name: "Shopify",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/shopify_w87tn3.svg",
        },
        {
            name: "Microsoft",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/gmail_1_zya4ai.svg",
        },
        {
            name: "Amazon",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Gmail_icon__2020_2_1_b0o8gy.svg",
        },
        {
            name: "Quickbooks-desktop",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame_2611386_ievdjs.svg",
        },
        {
            name: "Sps-commerce",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/sps_commerce_v0uhiz.png",
        },
        {
            name: "Klaviyo",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/Klaviyo_symbol_zynlc7.webp",
        },
        {
            name: "Google-Ads",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/google-ads_syiiti.svg",
        },
        {
            name: "Tiktok-Shop",
            img: "https://storage.googleapis.com/heysynth_files/synth_webapp/tiktok-shop-logo.webp",
        },
    ];

    const isIntegrationNameCorrect = SocialData?.find(
        (item) => item?.name?.toLowerCase() === integrationName
    );

    useEffect(() => {
        if (quickBooksConnected?.active && integrationName.toLowerCase() === "quickbooks") {
            dispatch(integrationActions.getQuickbooksExpenseList());
            dispatch(integrationActions?.getQuickbooksIncomeAccounts());
            dispatch(integrationActions.getQuickbooksApList());
            dispatch(integrationActions?.getQuickbooksAssetAccounts());
        }
    }, [dispatch, quickBooksConnected?.active, integrationName]);

    useEffect(() => {
        if (qbDesktop?.active && integrationName.toLowerCase() === "quickbooks-desktop") {
            dispatch(integrationActions.getQbDesktopApAccounts());
            dispatch(integrationActions.getQbDesktopCurrentAssetAccounts());
            dispatch(integrationActions.getQbDesktopFixedAssetAccounts());
            dispatch(integrationActions.getQbDesktopExpenseAccounts());
            dispatch(integrationActions.getQbDesktopIncomeAccounts());
        }
    }, [dispatch, qbDesktop?.active, integrationName]);

    useEffect(() => {
        const mergedArray = [
            { "Expense Account(s)": expenseAccounts || [] },
            { "Payable Account(s)": payableAccounts || [] },
            { "Income Account(s)": incomeAccounts || [] },
            { "Asset Account(s)": assetAccounts || [] },
        ];

        const mergedQbDesktopArray = [
            { "Expense Account(s)": expenseAccounts || [] },
            { "Payable Account(s)": payableAccounts || [] },
            { "Income Account(s)": incomeAccounts || [] },
            { "Current Asset Account(s)": assetAccounts || [] },
            // { "Fixed Asset Account(s)": fixedAssetAccounts || [] },
        ];

        setFormattedArray(
            integrationName.toLowerCase() === "quickbooks-desktop"
                ? mergedQbDesktopArray
                : mergedArray
        );
    }, [
        expenseAccounts,
        payableAccounts,
        incomeAccounts,
        assetAccounts,
        fixedAssetAccounts,
        integrationName,
    ]);

    useEffect(() => {
        if (Boolean(fetchedQuickbooksApAccountsSuccess)) {
            setPayableAccounts(fetchedQuickbooksApAccountsSuccess?.accounts);
        }
    }, [fetchedQuickbooksApAccountsSuccess]);
    useEffect(() => {
        if (Boolean(fetchedQuickbooksExpenseAccountsSuccess)) {
            setExpenseAccounts(fetchedQuickbooksExpenseAccountsSuccess?.accounts);
        }
    }, [fetchedQuickbooksExpenseAccountsSuccess]);
    useEffect(() => {
        if (Boolean(fetchedQuickbooksIncomeAccountsSuccess)) {
            setIncomeAccounts(fetchedQuickbooksIncomeAccountsSuccess?.accounts);
        }
    }, [fetchedQuickbooksIncomeAccountsSuccess]);
    useEffect(() => {
        if (Boolean(fetchedQuickbooksAssetAccountsSuccess)) {
            setAssetAccounts(fetchedQuickbooksAssetAccountsSuccess?.accounts);
        }
    }, [fetchedQuickbooksAssetAccountsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedQbDesktopExpenseAccountsSuccess)) {
            setExpenseAccounts(fetchedQbDesktopExpenseAccountsSuccess?.accounts);
        }
    }, [fetchedQbDesktopExpenseAccountsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedQbDesktopIncomeAccountsSuccess)) {
            setIncomeAccounts(fetchedQbDesktopIncomeAccountsSuccess?.accounts);
        }
    }, [fetchedQbDesktopIncomeAccountsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedQbDesktopCurrentAssetAccountsSuccess)) {
            setAssetAccounts(fetchedQbDesktopCurrentAssetAccountsSuccess?.accounts);
        }
    }, [fetchedQbDesktopCurrentAssetAccountsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedQbDesktopFixedAssetAccountsSuccess)) {
            setFixedAssetAccounts(fetchedQbDesktopFixedAssetAccountsSuccess?.accounts);
        }
    }, [fetchedQbDesktopFixedAssetAccountsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedQbDesktopApAccountsSuccess)) {
            setPayableAccounts(fetchedQbDesktopApAccountsSuccess?.accounts);
        }
    }, [fetchedQbDesktopApAccountsSuccess]);

    useEffect(() => {
        if (syncedAccount) {
            const timeoutId = setTimeout(() => {
                setSyncedAccount(false);
            }, 2000);

            return () => clearTimeout(timeoutId);
        }
    }, [syncedAccount]);
    useEffect(() => {
        if (Boolean(setQuickbooksAccountSuccess)) {
            dispatch(vendorActions.getAllChannels());
            dispatch(integrationActions.resetSetUpQuickbooksSuccess());
            setSyncedAccount(true);
        }
    }, [setQuickbooksAccountSuccess]);

    useEffect(() => {
        const requiredKeys = [
            "Payable Account(s)",
            "Expense Account(s)",
            "Income Account(s)",
            "Asset Account(s)",
        ];

        const requiredQbDesktopKeys = [
            "Payable Account(s)",
            "Expense Account(s)",
            "Income Account(s)",
            "Current Asset Account(s)",
            // "Fixed Asset Account(s)",
        ];

        const hasAll = (
            integrationName.toLowerCase() === "quickbooks-desktop"
                ? requiredQbDesktopKeys
                : requiredKeys
        ).every((key) => selectedAccounts.some((item) => Object.keys(item)[0] === key));
        setShowButton(hasAll);
    }, [selectedAccounts, socialAccount, integrationName]);

    const handleUpdate = () => {
        const qbAPAccountRef = selectedAccounts?.find(
            (item) => Object.keys(item)[0] === "Payable Account(s)"
        );
        const qbExpenseAccountRef = selectedAccounts?.find(
            (item) => Object.keys(item)[0] === "Expense Account(s)"
        );
        const qbIncomeAccountRef = selectedAccounts?.find(
            (item) => Object.keys(item)[0] === "Income Account(s)"
        );
        const qbAssetAccountRef = selectedAccounts?.find(
            (item) => Object.keys(item)[0] === "Asset Account(s)"
        );
        const qbCurrentAssetAccountRef = selectedAccounts?.find(
            (item) => Object.keys(item)[0] === "Current Asset Account(s)"
        );
        // const qbFixedAssetAccountRef = selectedAccounts?.find(
        //     (item) => Object.keys(item)[0] === "Fixed Asset Account(s)"
        // );

        const body: any = {};

        if (integrationName?.toLowerCase() === "quickbooks-desktop") {
            if (qbAPAccountRef?.["Payable Account(s)"]?.Id) {
                body.qbDesktopAPAccountRef = {
                    id: qbAPAccountRef["Payable Account(s)"].Id,
                    name: qbAPAccountRef["Payable Account(s)"].Name,
                };
            }

            if (qbExpenseAccountRef?.["Expense Account(s)"]?.Id) {
                body.qbDesktopCOGAccountRef = {
                    id: qbExpenseAccountRef["Expense Account(s)"].Id,
                    name: qbExpenseAccountRef["Expense Account(s)"].Name,
                };
            }

            if (qbIncomeAccountRef?.["Income Account(s)"]?.Id) {
                body.qbDesktopIncomeAccountRef = {
                    id: qbIncomeAccountRef["Income Account(s)"].Id,
                    name: qbIncomeAccountRef["Income Account(s)"].Name,
                };
            }

            if (qbCurrentAssetAccountRef?.["Current Asset Account(s)"]?.Id) {
                body.qbDesktopCurrentAssetAccountRef = {
                    id: qbCurrentAssetAccountRef["Current Asset Account(s)"].Id,
                    name: qbCurrentAssetAccountRef["Current Asset Account(s)"].Name,
                };
            }

            // if (qbFixedAssetAccountRef?.["Fixed Asset Account(s)"]?.Id) {
            //     body.qbDesktopFixedAssetAccountRef = {
            //         id: qbFixedAssetAccountRef["Fixed Asset Account(s)"].Id,
            //         name: qbFixedAssetAccountRef["Fixed Asset Account(s)"].Name,
            //     };
            // }
        } else {
            if (qbAPAccountRef?.["Payable Account(s)"]?.Id) {
                body.qbAPAccountRef = {
                    id: qbAPAccountRef["Payable Account(s)"].Id,
                    name: qbAPAccountRef["Payable Account(s)"].Name,
                };
            }

            if (qbExpenseAccountRef?.["Expense Account(s)"]?.Id) {
                body.qbExpenseAccountRef = {
                    id: qbExpenseAccountRef["Expense Account(s)"].Id,
                    name: qbExpenseAccountRef["Expense Account(s)"].Name,
                };
            }

            if (qbIncomeAccountRef?.["Income Account(s)"]?.Id) {
                body.qbIncomeAccountRef = {
                    id: qbIncomeAccountRef["Income Account(s)"].Id,
                    name: qbIncomeAccountRef["Income Account(s)"].Name,
                };
            }

            if (qbAssetAccountRef?.["Asset Account(s)"]?.Id) {
                body.qbAssetAccountRef = {
                    id: qbAssetAccountRef["Asset Account(s)"].Id,
                    name: qbAssetAccountRef["Asset Account(s)"].Name,
                };
            }
        }

        if (body) {
            dispatch(integrationActions.updateSocialAccount(body, integrationId));
        }
    };

    useEffect(() => {
        if (Boolean(updateSocialAccountSuccess)) {
            setSelectedAccounts([]);
            dispatch(integrationActions.fetchSocialAccount(integrationId));
            if (quickBooksConnected?.active) {
                dispatch(integrationActions.getQuickbooksExpenseList());
                dispatch(integrationActions.getQuickbooksApList());
            }
            dispatch(integrationActions.resetUpdateSocialAccountSuccess());
        }
    }, [updateSocialAccountSuccess, integrationId, dispatch]);

    return (
        <div className="py-4 lg:border pb-20 border-slate-100 rounded-[8px]">
            <div className="px-6 max-lg:hidden block  pt-3 pb-5 border-b rounded border-slate-100">
                <div
                    className="flex items-center space-x-2 w-fit cursor-pointer"
                    onClick={() => goBack()}
                >
                    <i className="ri-arrow-left-line text-[20px]"></i>
                    <p className="text-sm font-rocGroteskMedium underline">Back</p>
                </div>
            </div>
            {isIntegrationNameCorrect ? (
                <>
                    <div className="px-6 border-b rounded  py-3">
                        <div className="xs:flex-row flex-col flex justify-between">
                            <div>
                                {SocialData?.map((data, idx) => (
                                    <div key={idx}>
                                        {data.name.toLowerCase() === integrationName && (
                                            <div key={idx} className="flex items-center space-x-2">
                                                <img
                                                    src={data.img}
                                                    alt={data.name}
                                                    className="w-[44px] h-[44px]"
                                                />
                                                <p className="text-base text-[#0F172A] capitalize font-rocGroteskMedium">
                                                    {data?.name}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <div className="w-fit mt-3 bg-g-25 py-[2px] px-[8px] rounded">
                                    <p className="text-xs text-g-60 font-rocGroteskMedium">
                                        Connected account
                                    </p>
                                </div>
                            </div>
                            <div className="mt-3 sm:block hidden">
                                <Button
                                    btnText={`Disconnect ${
                                        integrationName.charAt(0).toUpperCase() +
                                        integrationName.slice(1)
                                    }`}
                                    type={"button"}
                                    btnPermission={[
                                        AccountPermissions.all_access,
                                        AccountPermissions.manage_integrations,
                                    ]}
                                    isLoading={manageConnection}
                                    icon={<i className="ri-indeterminate-circle-line"></i>}
                                    btnClassname="!py-2  !bg-btn-light-red_3 !border !border-r-55 !text-[14px] !text-r-55 !px-4"
                                    onClick={() => setDisconnected(true)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="lg:px-6 mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {(integrationName.toLowerCase() === "quickbooks" ||
                            integrationName.toLowerCase() === "quickbooks-desktop") && (
                            <>
                                <Paper className="lg:bg-white p-3 lg:p-6">
                                    <div>
                                        <p className="text-[12px] uppercase tracking-[2px] leading-[17.86px] font-rocGroteskMedium">
                                            Accounts
                                        </p>

                                        <div className="mt-6 mb-6">
                                            <p className="md:text-[16px] text-sm font-rocGroteskMedium text-gm-50 mt-6">
                                                Sync POs on Synth with QuickBooks online{" "}
                                            </p>
                                            <p className="text-gm-75 text-xs font-rocGroteskMedium mt-1 text-[#64748B]">
                                                When enabled, any purchase order created or updated
                                                in Synth will be automatically created or updated in
                                                QuickBooks online in real time, no manual entry
                                                needed.
                                            </p>

                                            <div className="mt-3">
                                                <Checkbox
                                                    checked={shouldAutoSyncPOs}
                                                    onClick={(e) => {
                                                        setShouldAutoSyncPOs((prev) => !prev);
                                                    }}
                                                    label={
                                                        <p className="text-gm-45 text-sm font-rocGroteskMedium">
                                                            Automatically sync POs to QuickBooks
                                                        </p>
                                                    }
                                                />

                                                {shouldAutoSyncPOs && (
                                                    <div className="mt-2">
                                                        <SelectInputWithSearch
                                                            disabled={fetchingPOSettings}
                                                            name="quickBooksOnlineAPAccount"
                                                            inputPlaceholder="Select chart of account"
                                                            dropdownOptions={getChartOfAccountsQuery?.data?.map(
                                                                (item) => ({
                                                                    label: item?.Name,
                                                                    subText: `Account ID: ${item?.Id}`,
                                                                    value: item?.Id,
                                                                })
                                                            )}
                                                            handleChange={(name, value) => {
                                                                setQuickBooksOnlineAPAccount({
                                                                    name:
                                                                        getChartOfAccountsQuery?.data?.find(
                                                                            (item) =>
                                                                                item?.Id === value
                                                                        )?.Name || "",
                                                                    id: value,
                                                                });
                                                            }}
                                                            inputClassName=""
                                                            value={
                                                                quickBooksOnlineAPAccount?.id || ""
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-5 border-t  border-t-slate-200 mb-5">
                                            <div className="mt-[24px]">
                                                <p className="text-base font-rocGroteskMedium text-g-75">
                                                    Sync sales orders on Synth with QuickBooks
                                                    online
                                                </p>

                                                <p className="text-[#64748B] text-xs text-g-75">
                                                    When enabled, any sales orders created or
                                                    updated in Synth will be automatically created
                                                    or updated in QuickBooks online in real time, no
                                                    manual entry needed.
                                                </p>

                                                <div className="mt-[8px]">
                                                    <Checkbox
                                                        checked={syncSalesOrders}
                                                        onClick={(e) => {
                                                            const newValue = !syncSalesOrders;
                                                            setSyncSalesOrders(newValue);
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                quickBooksOnline: {
                                                                    ...prev.quickBooksOnline,
                                                                    shouldAutoSyncSales: newValue,
                                                                },
                                                            }));
                                                        }}
                                                        label={
                                                            <p className="text-gm-45 text-sm font-rocGroteskMedium">
                                                                Automatically sync sales orders to
                                                                QuickBooks
                                                            </p>
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {syncSalesOrders && (
                                                <div className="mt-[24px]">
                                                    <div className="flex flex-col gap-4">
                                                        <SelectInputWithSearch
                                                            name="shopify"
                                                            label="Shopify"
                                                            placeholder="Select deposit bank account"
                                                            disabled={
                                                                fetchingSalesSettings ||
                                                                updatingSalesSettings
                                                            }
                                                            handleChange={(name, value) => {
                                                                handleChannelSync("Shopify", value);
                                                            }}
                                                            dropdownOptions={[
                                                                ...getBankAccountsQuery?.data?.map(
                                                                    (item) => ({
                                                                        label: item?.Name,
                                                                        subText: `Account ID: ${item?.Id}`,
                                                                        value: item?.Id,
                                                                    })
                                                                ),
                                                                {
                                                                    label: "None",
                                                                    subText:
                                                                        "Don't sync any account",
                                                                    value: "None",
                                                                },
                                                            ]}
                                                            value={
                                                                formData?.quickBooksOnline?.channelsToSync?.find(
                                                                    (item) =>
                                                                        item.channel === "Shopify"
                                                                )?.depositBankAccountRef?.id ||
                                                                "None"
                                                            }
                                                        />

                                                        <SelectInputWithSearch
                                                            name="amazon"
                                                            label="Amazon"
                                                            placeholder="Select deposit bank account"
                                                            disabled={
                                                                fetchingSalesSettings ||
                                                                updatingSalesSettings
                                                            }
                                                            handleChange={(name, value) => {
                                                                handleChannelSync("Amazon", value);
                                                            }}
                                                            dropdownOptions={[
                                                                ...getBankAccountsQuery?.data?.map(
                                                                    (item) => ({
                                                                        label: item?.Name,
                                                                        subText: `Account ID: ${item?.Id}`,
                                                                        value: item?.Id,
                                                                    })
                                                                ),
                                                                {
                                                                    label: "None",
                                                                    subText:
                                                                        "Don't sync any account",
                                                                    value: "None",
                                                                },
                                                            ]}
                                                            value={
                                                                formData?.quickBooksOnline?.channelsToSync?.find(
                                                                    (item) =>
                                                                        item.channel === "Amazon"
                                                                )?.depositBankAccountRef?.id ||
                                                                "None"
                                                            }
                                                        />

                                                        <SelectInputWithSearch
                                                            name="retail"
                                                            label="Retail"
                                                            placeholder="Select deposit bank account"
                                                            disabled={
                                                                fetchingSalesSettings ||
                                                                updatingSalesSettings
                                                            }
                                                            dropdownOptions={[
                                                                ...getBankAccountsQuery?.data?.map(
                                                                    (item) => ({
                                                                        label: item?.Name,
                                                                        subText: `Account ID: ${item?.Id}`,
                                                                        value: item?.Id,
                                                                    })
                                                                ),
                                                                {
                                                                    label: "None",
                                                                    subText:
                                                                        "Don't sync any account",
                                                                    value: "None",
                                                                },
                                                            ]}
                                                            value={
                                                                formData?.quickBooksOnline?.channelsToSync?.find(
                                                                    (item) =>
                                                                        item.channel === "Retail"
                                                                )?.depositBankAccountRef?.id ||
                                                                "None"
                                                            }
                                                            handleChange={(name, value) => {
                                                                handleChannelSync("Retail", value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <Button
                                                btnText="Save changes"
                                                btnClassname="!w-fit !py-2 mt-5"
                                                isLoading={
                                                    updatingSalesSettings || updatingSalesSettings
                                                }
                                                onClick={handleUpdateSalesAndPoSettings}
                                            />
                                        </div>
                                    </div>
                                </Paper>
                            </>
                        )}

                        <Paper className="lg:bg-white h-full p-6">
                            <div>
                                <p className="text-base font-rocGroteskMedium text-gm-50">
                                    Here are the types of information that Synth is getting from
                                    your accounts{" "}
                                </p>
                                {/* <p className="text-[#6B778C] font-rocGroteskMedium text-sm">
                                    Here are the types of information that Synth is getting from
                                    your accounts
                                </p> */}
                            </div>
                            <div className="mt-3">
                                <div className="py-[16px] border-b border-[#EBECF0] flex justify-between">
                                    <div className="flex space-x-3  ">
                                        <div>
                                            <div className="h-[38px] w-[38px] rounded-[200px] bg-slate-50 flex justify-center items-center">
                                                <i className="ri-money-dollar-circle-fill text-slate-500 text-[22px] text-center"></i>
                                            </div>
                                        </div>
                                        <div className="text-sm font-rocGroteskMedium">
                                            <h6 className=" text-[#0F172A]">Sales information </h6>
                                            <p className="text-[#64748B] text-[12px]">
                                                E.g., sales volume, sales value, sales agents, sales
                                                channels, and any other relevant sales information
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="py-[16px] border-b border-[#EBECF0] flex justify-between">
                                    <div className="flex space-x-3  ">
                                        <div>
                                            <div className="h-[38px] w-[38px] rounded-[200px] bg-slate-50 flex justify-center items-center">
                                                <i className="ri-shopping-basket-2-fill text-slate-500 text-[22px] text-center"></i>
                                            </div>
                                        </div>
                                        <div className="text-sm font-rocGroteskMedium">
                                            <h6 className=" text-[#0F172A]">
                                                Inventory information{" "}
                                            </h6>
                                            <p className="text-[#64748B] text-[12px]">
                                                E.g., inventory volume, inventory value, retail
                                                stores, inventory managers, stock-outs, over-stocked
                                                products, and any other relevant inventory
                                                information.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {(integrationName.toLowerCase() === "google" ||
                                    integrationName.toLowerCase() === "microsoft" ||
                                    integrationName.toLowerCase() === "quickbooks") && (
                                    <div className="py-[16px] border-b border-[#EBECF0] flex justify-between">
                                        <div className="flex space-x-3  ">
                                            <div>
                                                <div className="h-[38px] w-[38px] rounded-[200px] bg-slate-50 flex justify-center items-center">
                                                    <i className="ri-shopping-basket-2-fill text-slate-500 text-[22px] text-center"></i>
                                                </div>
                                            </div>
                                            <div className="text-sm font-rocGroteskMedium">
                                                <h6 className=" text-[#0F172A]">
                                                    Purchase orders (PO) information{" "}
                                                </h6>
                                                <p className="text-[#64748B] text-[12px]">
                                                    E.g., suppliers, PO count, PO value, PO status,
                                                    shipment tracking information, and any other
                                                    relevant PO information.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Paper>
                    </div>
                </>
            ) : (
                <div className="items-center flex  h-[100%]  justify-center my-[20%]">
                    <div className="space-y-2  text-center">
                        <i className="ri-file-list-3-line text-gm-50 text-[24px]"></i>

                        <p className="text-slate-500 font-rocGroteskMedium text-[12px]">
                            {integrationName} integration is not found
                        </p>
                    </div>
                </div>
            )}

            <div className="mt-3 sm:hidden block fixed z-[20] left-0 right-0 px-6 bottom-0 bg-white py-3">
                <Button
                    btnText={`Disconnect ${
                        integrationName.charAt(0).toUpperCase() + integrationName.slice(1)
                    }`}
                    btnPermission={[
                        AccountPermissions.all_access,
                        AccountPermissions.manage_integrations,
                    ]}
                    type={"button"}
                    isLoading={manageConnection}
                    icon={<i className="ri-indeterminate-circle-line"></i>}
                    btnClassname="!py-2  !bg-btn-light-red_3 !border !border-r-55 !text-[14px] !text-r-55 !px-4"
                    onClick={() => setDisconnected(true)}
                />
            </div>
        </div>
    );
};

export default IntegrationDetails;
